import type {
  AddGoalMilestoneInput,
  ChangeGoalStatusInput,
  CreateGoalCategoryInput,
  CreateGoalInput,
  DataResponse,
  GoalCategory,
  GoalConfigSettings,
  GoalDetail,
  GoalListItem,
  GoalSummary,
  GoalSweepResult,
  UpdateGoalConfigInput,
  ListGoalsQuery,
  LogGoalProgressInput,
  PaginatedResponse,
  ReviewGoalInput,
  ToggleGoalMilestoneInput,
  UpdateGoalCategoryInput,
  UpdateGoalInput,
} from '@pms/shared';
import { api } from '@/lib/api';

export const goalsService = {
  // ── Automation config ───────────────────────────────────────────────────────
  async getConfig(): Promise<GoalConfigSettings> {
    const res = await api.get<DataResponse<GoalConfigSettings>>('/goals/config/settings');
    return res.data.data;
  },

  async updateConfig(input: UpdateGoalConfigInput): Promise<void> {
    await api.patch('/goals/config/settings', input);
  },

  /** Runs the sweep immediately, bypassing the configured run-time gate. */
  async runSweep(): Promise<GoalSweepResult> {
    const res = await api.post<DataResponse<GoalSweepResult>>('/goals/admin/run-sweep', {});
    return res.data.data;
  },

  async summary(mine: boolean): Promise<GoalSummary> {
    const res = await api.get<DataResponse<GoalSummary>>('/goals/summary', { params: { mine } });
    return res.data.data;
  },

  async listGoals(query: Partial<ListGoalsQuery> = {}): Promise<PaginatedResponse<GoalListItem>> {
    const res = await api.get<PaginatedResponse<GoalListItem>>('/goals', { params: query });
    return res.data;
  },

  async getGoal(id: string): Promise<GoalDetail> {
    const res = await api.get<DataResponse<GoalDetail>>(`/goals/${id}`);
    return res.data.data;
  },

  /** Returns one id per owner — selecting several employees creates a goal for each. */
  async createGoal(input: CreateGoalInput): Promise<{ ids: string[] }> {
    const res = await api.post<DataResponse<{ ids: string[] }>>('/goals', input);
    return res.data.data;
  },

  async updateGoal(id: string, input: UpdateGoalInput): Promise<void> {
    await api.patch(`/goals/${id}`, input);
  },

  async changeStatus(id: string, input: ChangeGoalStatusInput): Promise<void> {
    await api.patch(`/goals/${id}/status`, input);
  },

  async logProgress(id: string, input: LogGoalProgressInput): Promise<void> {
    await api.post(`/goals/${id}/progress`, input);
  },

  async toggleMilestone(id: string, milestoneId: string, input: ToggleGoalMilestoneInput): Promise<void> {
    await api.patch(`/goals/${id}/milestones/${milestoneId}`, input);
  },

  async addMilestone(id: string, input: AddGoalMilestoneInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>(`/goals/${id}/milestones`, input);
    return res.data.data;
  },

  async deleteMilestone(id: string, milestoneId: string): Promise<void> {
    await api.delete(`/goals/${id}/milestones/${milestoneId}`);
  },

  async reviewGoal(id: string, input: ReviewGoalInput): Promise<void> {
    await api.patch(`/goals/${id}/review`, input);
  },

  async deleteGoal(id: string): Promise<void> {
    await api.delete(`/goals/${id}`);
  },

  async listCategories(activeOnly = true): Promise<GoalCategory[]> {
    const res = await api.get<DataResponse<GoalCategory[]>>('/goals/categories', {
      params: { activeOnly },
    });
    return res.data.data;
  },

  async createCategory(input: CreateGoalCategoryInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/goals/categories', input);
    return res.data.data;
  },

  async updateCategory(id: string, input: UpdateGoalCategoryInput): Promise<void> {
    await api.patch(`/goals/categories/${id}`, input);
  },

  async deleteCategory(id: string): Promise<void> {
    await api.delete(`/goals/categories/${id}`);
  },
};
